import { FEW_TICKERS } from './types';
import type { AdapterOHLCVRequest, AdapterOptions, ExchangeAdapter } from './types';
import { ReconnectingSocket } from './ws';
import {
  candlesInWindow,
  decimalsOf,
  mapLimit,
  PAGE_CONCURRENCY,
  pageWindows,
  parseJson,
  rollUpStream,
} from './binance';
import { ProviderError } from '../types';
import type { StreamStatus, Unsubscribe } from '../types';
import type { TradingPair } from '../../types/pair';
import type { OHLCV, Timeframe } from '../../types/ohlcv';
import type { Ticker24h } from '../../types/ticker';
import { aggregateToTimeframe, bucketStart } from '../../utils/aggregate';
import { timeframeToMs } from '../../utils/timeframe';

/**
 * Bybit v5 spot public market data (no API key; CORS echoes the page origin).
 * Endpoint facts this adapter relies on, verified live 2026-10-02:
 *   - GET /v5/market/kline?category=spot&symbol&interval&start&end&limit
 *     returns `result.list` rows `[start, open, high, low, close, volume,
 *     turnover]` as strings, NEWEST first. It answers every candle that
 *     overlaps [start, end] (a mid-candle `start` brings the candle before
 *     it), keeps the newest rows when there are more than `limit` (max
 *     1000), and with `start` alone answers the newest rows — so every page
 *     names both ends.
 *   - Intervals 1 3 5 15 30 60 120 240 360 720 D W M; W opens on Monday and M
 *     on the 1st (UTC). There is no 3-day interval.
 *   - Request errors (unknown symbol, bad interval) come back as HTTP 200 with
 *     a non-zero `retCode` and a `retMsg`.
 *   - instruments-info?category=spot returns every instrument in one response
 *     (spot ignores `limit` and has no cursor); symbol is baseCoin + quoteCoin.
 *   - tickers?category=spot: `price24hPcnt` is a fraction (0.031 = +3.1 %),
 *     `volume24h` is base, `turnover24h` quote volume.
 *   - wss://stream.bybit.com/v5/public/spot takes at most 10 topics per
 *     subscribe request and expects `{"op":"ping"}` every 20 s.
 */
const REST_URL = 'https://api.bybit.com/v5/market';
const STREAM_URL = 'wss://stream.bybit.com/v5/public/spot';
const MAX_KLINES = 1000;
const MAX_TOPICS_PER_REQUEST = 10;
const PAIRS_TTL_MS = 10 * 60_000;
const HEARTBEAT = { intervalMs: 20_000, message: JSON.stringify({ op: 'ping' }) };

const INTERVAL: Partial<Record<Timeframe, string>> = {
  '1m': '1',
  '3m': '3',
  '5m': '5',
  '15m': '15',
  '30m': '30',
  '1h': '60',
  '2h': '120',
  '4h': '240',
  '6h': '360',
  '12h': '720',
  '1D': 'D',
  '1W': 'W',
  '1M': 'M',
};

/** Timeframes rolled up from a finer native one (fixed-length timeframes only). */
const DERIVED: Partial<Record<Timeframe, Timeframe>> = {
  '3D': '1D',
};

export function toBybitSymbol(pair: string): string {
  return pair.trim().toUpperCase().replace('/', '');
}

export function mapBybitKlines(list: unknown): OHLCV[] {
  if (!Array.isArray(list)) throw new ProviderError('Unrecognized Bybit kline response', undefined, list);
  const out: OHLCV[] = [];
  for (const row of list) {
    if (!Array.isArray(row) || row.length < 6) continue;
    out.push({
      timestamp: Number(row[0]),
      open: Number(row[1]),
      high: Number(row[2]),
      low: Number(row[3]),
      close: Number(row[4]),
      volume: Number(row[5]),
    });
  }
  // Newest-first upstream; HopCharts wants oldest-first.
  return out.sort((a, b) => a.timestamp - b.timestamp);
}

interface BybitInstrument {
  baseCoin: string;
  quoteCoin: string;
  status: string;
  lotSizeFilter: { basePrecision?: string };
  priceFilter: { tickSize?: string };
}

function mapBybitPairs(list: unknown): TradingPair[] {
  if (!Array.isArray(list)) throw new ProviderError('Unrecognized Bybit instruments response', undefined, list);
  return (list as BybitInstrument[])
    .filter((i) => i.status === 'Trading')
    .map((i) => ({
      symbol: `${i.baseCoin}/${i.quoteCoin}`,
      base: i.baseCoin,
      quote: i.quoteCoin,
      exchangeId: 'bybit',
      priceDecimals: decimalsOf(i.priceFilter?.tickSize),
      quantityDecimals: decimalsOf(i.lotSizeFilter?.basePrecision),
    }))
    .sort((a, b) => a.symbol.localeCompare(b.symbol));
}

/** A spot ticker as REST lists it; stream snapshots carry the same fields minus bid/ask. */
interface BybitTicker {
  symbol: string;
  lastPrice: string;
  prevPrice24h: string;
  price24hPcnt: string;
  highPrice24h: string;
  lowPrice24h: string;
  volume24h: string;
  turnover24h?: string;
  bid1Price?: string;
  ask1Price?: string;
}

/** Null when the market has no last price. */
function toTicker(pair: string, row: BybitTicker, timestamp: number): Ticker24h | null {
  const last = Number(row.lastPrice);
  if (!(last > 0)) return null;
  const baseVolume = Number(row.volume24h);
  const quoteVolume = Number(row.turnover24h);
  return {
    exchangeId: 'bybit',
    pair,
    last,
    open24h: Number(row.prevPrice24h),
    high24h: Number(row.highPrice24h),
    low24h: Number(row.lowPrice24h),
    changePercent24h: Number(row.price24hPcnt) * 100,
    baseVolume24h: baseVolume,
    quoteVolume24h: Number.isFinite(quoteVolume) ? quoteVolume : baseVolume * last,
    bid: row.bid1Price ? Number(row.bid1Price) : undefined,
    ask: row.ask1Price ? Number(row.ask1Price) : undefined,
    timestamp,
  };
}

interface BybitFrame {
  topic?: string;
  data?: unknown;
  ts?: number;
  /** Present on replies to subscribe and ping requests. */
  success?: boolean;
}

function candleFromKline(item: unknown): OHLCV | null {
  const k = item as { start?: number; open: string; high: string; low: string; close: string; volume: string } | null;
  if (typeof k?.start !== 'number') return null;
  return {
    timestamp: k.start,
    open: Number(k.open),
    high: Number(k.high),
    low: Number(k.low),
    close: Number(k.close),
    volume: Number(k.volume),
  };
}

export function bybitAdapter(options: AdapterOptions = {}): ExchangeAdapter {
  let pairsCache: { at: number; pairs: Promise<TradingPair[]> } | null = null;

  async function getResult(path: string): Promise<{ result: unknown; time: number }> {
    const doFetch = options.fetch ?? globalThis.fetch;
    let response: Response;
    try {
      response = await doFetch(`${REST_URL}${path}`, { headers: { Accept: 'application/json' } });
    } catch (error) {
      throw new ProviderError(`Bybit is unreachable: ${error instanceof Error ? error.message : String(error)}`);
    }
    const body = (await response.json().catch(() => null)) as
      | { retCode?: number; retMsg?: string; result?: unknown; time?: number }
      | null;
    if (!response.ok) {
      throw new ProviderError(body?.retMsg || `Bybit request failed (${response.status})`, response.status, body);
    }
    // Request errors arrive as HTTP 200; there is no HTTP status to report for them.
    if (!body || body.retCode !== 0) {
      throw new ProviderError(body?.retMsg || 'Unrecognized Bybit response', undefined, body);
    }
    return { result: body.result, time: body.time ?? Date.now() };
  }

  async function fetchKlines(symbol: string, timeframe: Timeframe, from: number, to: number): Promise<OHLCV[]> {
    const interval = INTERVAL[timeframe];
    if (!interval) throw new ProviderError(`Unsupported timeframe ${timeframe}`);
    const windows = pageWindows(from, to, timeframe, MAX_KLINES);
    const pages = await mapLimit(windows, PAGE_CONCURRENCY, async ({ start, end }) => {
      const query = new URLSearchParams({
        category: 'spot',
        symbol,
        interval,
        start: String(start),
        end: String(end),
        limit: String(MAX_KLINES),
      });
      const { result } = await getResult(`/kline?${query}`);
      return mapBybitKlines((result as { list?: unknown } | null)?.list);
    });
    // Pages overlap by the candle containing each page's start; this also drops the one before `from`.
    return candlesInWindow(pages, from, to);
  }

  function loadPairs(): Promise<TradingPair[]> {
    if (pairsCache && Date.now() - pairsCache.at < PAIRS_TTL_MS) return pairsCache.pairs;
    const pairs = getResult('/instruments-info?category=spot').then(({ result }) =>
      mapBybitPairs((result as { list?: unknown } | null)?.list),
    );
    const entry = { at: Date.now(), pairs };
    pairsCache = entry;
    // A failed load must not be cached.
    pairs.catch(() => {
      if (pairsCache === entry) pairsCache = null;
    });
    return pairs;
  }

  /** One socket for `topics`, subscribed again on every connect. */
  function openTopics(
    topics: string[],
    onFrame: (frame: BybitFrame) => void,
    onStatus?: (status: StreamStatus) => void,
    onConnect?: () => void,
  ): ReconnectingSocket {
    return new ReconnectingSocket({
      urls: [STREAM_URL],
      onOpen: (send) => {
        for (let i = 0; i < topics.length; i += MAX_TOPICS_PER_REQUEST) {
          send(JSON.stringify({ op: 'subscribe', args: topics.slice(i, i + MAX_TOPICS_PER_REQUEST) }));
        }
        onConnect?.();
      },
      onMessage: (text) => {
        const frame = parseJson(text) as BybitFrame | undefined;
        if (frame?.success === false) onStatus?.('error');
        else if (frame?.topic) onFrame(frame);
      },
      onStatus,
      heartbeat: HEARTBEAT,
      WebSocket: options.WebSocket,
    });
  }

  const adapter: ExchangeAdapter = {
    id: 'bybit',
    name: 'Bybit',

    getPairs: loadPairs,

    async getOHLCV({ pair, timeframe, from, to }: AdapterOHLCVRequest): Promise<OHLCV[]> {
      const symbol = toBybitSymbol(pair);
      const source = DERIVED[timeframe];
      if (!source) return fetchKlines(symbol, timeframe, from, to);
      // Whole buckets only: from the open of the first bucket to the last millisecond of the last.
      const lastBucketEnd = bucketStart(to, timeframe) + timeframeToMs(timeframe) - 1;
      const fine = await fetchKlines(symbol, source, bucketStart(from, timeframe), lastBucketEnd);
      return aggregateToTimeframe(fine, timeframe).filter((c) => c.timestamp >= from && c.timestamp <= to);
    },

    async getTickers(wanted?: string[]): Promise<Ticker24h[]> {
      if (wanted && wanted.length > 0 && wanted.length <= FEW_TICKERS) {
        // The caller named the pairs, so no instrument list is needed to map symbols. A market Bybit does
        // not list answers with an error code; it is left out rather than failing the others.
        const rows = await Promise.all(
          wanted.map((pair) =>
            getResult(`/tickers?category=spot&symbol=${toBybitSymbol(pair)}`)
              .then(({ result, time }) => {
                const row = ((result as { list?: unknown } | null)?.list as BybitTicker[] | undefined)?.[0];
                return row ? toTicker(pair, row, time) : null;
              })
              .catch(() => null),
          ),
        );
        return rows.filter((t): t is Ticker24h => t !== null);
      }
      const [pairs, { result, time }] = await Promise.all([loadPairs(), getResult('/tickers?category=spot')]);
      const list = (result as { list?: unknown } | null)?.list;
      if (!Array.isArray(list)) throw new ProviderError('Unrecognized Bybit ticker response', undefined, result);
      const pairBySymbol = new Map(pairs.map((p) => [p.base + p.quote, p.symbol]));
      const tickers: Ticker24h[] = [];
      for (const row of list as BybitTicker[]) {
        const pair = pairBySymbol.get(row.symbol);
        const ticker = pair ? toTicker(pair, row, time) : null;
        if (ticker) tickers.push(ticker);
      }
      return tickers;
    },

    subscribeOHLCV(pair, timeframe, onCandle, onStatus): Unsubscribe {
      const source = DERIVED[timeframe];
      const rollUp = source
        ? rollUpStream(
            timeframe,
            (from, to) => adapter.getOHLCV({ pair, timeframe: source, from, to }),
            onCandle,
            onStatus,
          )
        : null;
      const topic = `kline.${INTERVAL[source ?? timeframe]}.${toBybitSymbol(pair)}`;
      const socket = openTopics(
        [topic],
        (frame) => {
          if (frame.topic !== topic || !Array.isArray(frame.data)) return;
          for (const item of frame.data) {
            const candle = candleFromKline(item);
            if (!candle) continue;
            if (rollUp) rollUp.push(candle);
            else onCandle(candle);
          }
        },
        onStatus,
        () => rollUp?.seed(),
      );
      return () => {
        rollUp?.stop();
        socket.close();
      };
    },

    subscribeTickers(pairs, onTicker): Unsubscribe {
      const pairBySymbol = new Map(pairs.map((pair) => [toBybitSymbol(pair), pair]));
      if (pairBySymbol.size === 0) return () => {};
      const socket = openTopics(
        [...pairBySymbol.keys()].map((symbol) => `tickers.${symbol}`),
        (frame) => {
          const data = frame.data as BybitTicker | undefined;
          const pair = frame.topic?.startsWith('tickers.') && data ? pairBySymbol.get(data.symbol) : undefined;
          const ticker = pair && data ? toTicker(pair, data, frame.ts ?? Date.now()) : null;
          if (ticker) onTicker(ticker);
        },
      );
      return () => socket.close();
    },
  };

  return adapter;
}
